import { useResetAtom } from 'jotai/utils';
import { useTranslation } from 'react-i18next';
import { useSetAtom, useAtomValue } from 'jotai';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { paths } from 'src/routes/paths';

import store from 'src/store';

import { useModal } from './use-modal';
import { useRoute } from './use-route';

export default function useJoin() {
  const { openModal } = useModal();
  const { t } = useTranslation();
  const route = useRoute();

  const managerInfo = useAtomValue(store.manager.mangerInfoAtom);

  const BRAND_MANAGER_EMAIL = managerInfo?.email;
  const BRAND_MANAGER_PHONE = managerInfo?.phone;

  const join = useAtomValue(store.join.state);
  const setJoin = useSetAtom(store.join.state);
  const resetJoin = useResetAtom(store.join.state);

  // –------------------------ join store mutations methods –------------------------
  const getUserData = () => join;

  const initJoinStore = () => {
    resetJoin();
  };

  const setJoinStart = (joinStart: any) => {
    setJoin((prev) => ({ ...prev, joinStart }));
  };

  const setRole = (role: any) => {
    setJoin((prev) => ({ ...prev, role }));
  };

  const setDuty = (duty: any) => {
    setJoin((prev) => ({ ...prev, duty }));
  };

  const setDutyName = (dutyName: any) => {
    setJoin((prev) => ({ ...prev, dutyName }));
  };

  const setResponsibility = (responsibility: any) => {
    setJoin((prev) => ({ ...prev, responsibility }));
  };

  const setResponsibilityName = (responsibilityName: any) => {
    setJoin((prev) => ({ ...prev, responsibilityName }));
  };

  const setCode = (code: any) => {
    setJoin((prev) => ({ ...prev, code }));
  };

  const setTerms = (agreeDate: any) => {
    setJoin((prev) => ({
      ...prev,
      terms: {
        TERMS_OF_SERVICE: agreeDate,
        COLLECTION_AND_USE: agreeDate,
        PRIVACY_CONSIGNMENT: agreeDate,
        PRIVACY_STATEMENT: agreeDate,
        RULES_OF_WITHDRAWAL: agreeDate,
      },
    }));
  };

  const setNameFirst = (first: any) => {
    setJoin((prev) => {
      const { name } = prev;
      return {
        ...prev,
        name: { ...name, first },
      };
    });
  };

  const setNameLast = (last: any) => {
    setJoin((prev) => {
      const { name } = prev;
      return {
        ...prev,
        name: { ...name, last },
      };
    });
  };

  const setNameFull = (fullName: any) => {
    setJoin((prev) => ({
      ...prev,
      name: {
        ...prev.name,
        fullName,
      },
    }));
  };

  const setLoginId = (loginId: any) => {
    setJoin((prev) => ({ ...prev, loginId }));
  };

  const setLoginPw = (loginPw: any) => {
    setJoin((prev) => ({ ...prev, loginPw }));
  };

  const setLoginRepw = (loginRepw: any) => {
    setJoin((prev) => ({ ...prev, loginRepw }));
  };

  const setStore = (storeName: any) => {
    setJoin((prev) => ({ ...prev, store: storeName }));
  };

  const setSelectedBrand = (selectedBrand: any) => {
    setJoin((prev) => ({ ...prev, selectedBrand }));
  };

  const setSocialLink = (socialLink: any) => {
    setJoin((prev) => ({ ...prev, socialLink }));
  };

  const setSocialId = (socialId: any) => {
    setJoin((prev) => ({ ...prev, socialId }));
  };

  const setPushNotification = (option: any) => {
    setJoin((prev) => ({ ...prev, option }));
  };
  // –------------------------ End find pw store mutations –------------------------

  // –------------------------ join mixin functions –------------------------
  const cancelJoin = () => {
    openModal({
      type: 'confirm',
      data: {
        title: t('learner.1700'),
        content: t('learner.1701'),
        accept: t('learner.1703'),
        onAccept: () => {
          route.pushNavigate(paths.login);
        },
        cancel: t('learner.1702'),
        buttonColor: 'inherit',
        actionButtonsDirection: 'ltr',
        cancelButtonProps: { variant: 'outlined' },
      },
    });
  };

  const showMessage = (mode: 1 | 2) => {
    if (mode === 1) {
      openAlertDialog(
        t('learner.1588'),
        t('learner.1589'),
        t('learner.1590', { str: BRAND_MANAGER_EMAIL }),
        t('learner.1591', { str: BRAND_MANAGER_PHONE }),
        t('learner.1592')
      );
    } else if (mode === 2) {
      openAlertDialog(
        t('learner.1593'),
        t('learner.1594'),
        t('learner.1595', { str: BRAND_MANAGER_EMAIL }),
        t('learner.1596', { str: BRAND_MANAGER_PHONE }),
        t('learner.1592')
      );
    }
  };

  // –------------------------ End join mixin functions –------------------------

  const openAlertDialog = (
    title: string,
    content: string,
    managerEmail: string,
    managerPhone: string,
    accept: any,
    buttonColor: any = 'inherit'
  ) => {
    openModal({
      type: 'alert',
      data: {
        content: (
          <Stack spacing={2}>
            <Typography variant="body1">{content}</Typography>
            <Stack spacing={0.5} color="var(--text-secondary)">
              <Typography variant="subtitle1">{managerEmail}</Typography>
              <Typography variant="subtitle1">{managerPhone}</Typography>
            </Stack>
          </Stack>
        ),
        title,
        accept,
        buttonColor,
      },
    });
  };

  return {
    getUserData,
    initJoinStore,
    setJoinStart,
    setRole,
    setDuty,
    setDutyName,
    setResponsibility,
    setResponsibilityName,
    setCode,
    setTerms,
    setNameFirst,
    setNameLast,
    setNameFull,
    setLoginId,
    setLoginPw,
    setLoginRepw,
    setStore,
    setSelectedBrand,
    setSocialLink,
    setSocialId,
    setPushNotification,
    cancelJoin,
    showMessage,
  };
}
